import { useMemo } from 'react';

import type { BlockContext, BlockManifest } from '../../lib/cmsAdmin';
import { allowedChildren, canAcceptChildren, findNode, type BlockDocument } from '../../cms/editor/document';

/**
 * The palette of blocks that may be added.
 *
 * ── IT OFFERS ONLY WHAT WOULD BE ACCEPTED ────────────────────────────────────────────
 *
 * The list is filtered by the selected container's child rule and by the page's entity
 * kind. An EventHero cannot appear on a standalone page, and nothing can be added inside
 * a block whose manifest says it takes no children — so neither is offered. The server
 * enforces both on save; this stops an admin discovering the rule by being refused.
 */
export function Palette({
  document, manifests, selectedNodeId, context, onAdd,
}: {
  document: BlockDocument;
  manifests: BlockManifest[];
  selectedNodeId: string | null;
  context: BlockContext;
  onAdd: (component: string, parentId: string) => void;
}) {
  const selected = selectedNodeId ? findNode(document, selectedNodeId) : null;

  // Where a new block would go: into the selection when it accepts children, otherwise
  // into the selection's container, so clicking a leaf and adding still does the
  // obvious thing rather than refusing.
  const target = useMemo(() => {
    if (!document?.root) return null;
    if (selected && canAcceptChildren(selected)) return selected;
    if (!selected) return document.root;
    const findContainer = (node: BlockDocument['root'], childId: string): typeof node | null => {
      for (const child of node.children ?? []) {
        if (child.id === childId) return node;
        const deeper = findContainer(child, childId);
        if (deeper) return deeper;
      }
      return null;
    };
    return findContainer(document.root, selected.id) ?? document.root;
  }, [document, selected]);

  const allowed = useMemo(
    () => new Set(target ? allowedChildren(target, context) : []),
    [target, context],
  );

  const offered = manifests.filter((m) => allowed.has(m.id));
  const byCategory = offered.reduce<Record<string, BlockManifest[]>>((acc, m) => {
    (acc[m.category] ??= []).push(m);
    return acc;
  }, {});

  if (!target) return null;

  return (
    <div className="cms-palette">
      <p className="cms-palette__target">
        Adding into <strong>{target.id}</strong>
      </p>
      {offered.length === 0 && (
        <p className="adm-muted">
          This block does not take any content inside it. Select a Section or a Two Column
          Layout to add something.
        </p>
      )}
      {Object.entries(byCategory).map(([category, blocks]) => (
        <section key={category} className="cms-palette__group">
          <h4>{category}</h4>
          {blocks.map((block) => (
            <button
              key={block.id}
              type="button"
              className="cms-palette__block"
              onClick={() => onAdd(block.id, target.id)}
              title={block.dataRequirements.length > 0
                ? `Reads ${block.dataRequirements.join(', ')} from the event.`
                : undefined}
            >
              <span>{block.label}</span>
              {block.critical && <span className="badge badge--ok">Required</span>}
            </button>
          ))}
        </section>
      ))}
    </div>
  );
}
